<script setup>
const {
  leadGenData,
  ribbonFormData,
  checkInputFields,
  processLeadSubmission,
  validateWithZeroBounce
} = useLeadGenData();

const props = defineProps({
  ribbonTags: Array,
});

const { altFallback } = usePageAltFallback();
const getRibbonImageAlt = (image, title) =>
  resolveMediaAlt(image, title, altFallback.value);

let ribbonForm = null;
let leadGenFields = null;

const formData = ref(null);
const inputFirstName = ref("");
const inputEmail = ref("");
const inputPhoneNumber = ref("");
const inputPostalCode = ref("");
const errorFound = ref(false);
const errMsg = ref("");
const nameError = ref(false);
const emailError = ref(false);
const postalcodeError = ref(false);
const invalidEmail = ref(false);

const ages = [];
const currentYear = new Date();
for (let i = 7; i < 20; i++) {
  ages.push({ value: parseInt(currentYear.getFullYear()) - i, label: i });
}

const removeInvalidState = () => {
  invalidEmail.value = false
}

const emailErrorMessage = computed(() =>  {
  if (!leadGenFields?.emailFields) return "";
  return invalidEmail.value ? leadGenFields.emailFields.invalidEmailErrorMessage : leadGenFields.emailFields.emailErrorMessage;
})

async function submitHandler() {
  if (!ribbonForm || !leadGenFields || !formData.value?.elements) return;
  const formObject = { leadTypeID: 67000 }; // LeadTypeID is the ID for the brochure request
  const selectedLearningStyles = [];
  for (const element of formData.value.elements) {
    if (
      element.name === "" ||
      element.value === "" ||
      element.classList.contains("multi-checkbox")
    ) {
      continue;
    }
    if (element.type === "checkbox" && element.name === "learningStyleTypeIDs") {
      if (element.checked == 1) {
        selectedLearningStyles.push(element.value);
      }
      continue;
    }
    if (element.type === "checkbox") {
      if (!Array.isArray(formObject[element.name])) {
        formObject[element.name] = [element.value];
      } else {
        formObject[element.name].push(element.value);
      }
      continue;
    }
    if (element.name === "studentBirthYears") {
      formObject["students"] = element.value.split(",").map((year) => ({
        studentFirstName: "",
        studentBirthYear: parseInt(year, 10),
      }));
      continue;
    }
    formObject[element.name] = element.value;
  }

  const zeroBounceValidation = ribbonForm?.zeroBounceValidation || leadGenFields?.emailFields?.zeroBounceValidation;
  if (zeroBounceValidation) {
    const encodedEmail = encodeURIComponent(formObject.emailAddress)
    const { data } = await validateWithZeroBounce(encodedEmail)

    if (data.value?.status === 'invalid' ) {
      invalidEmail.value = true;
      return;
    }
  }

  formObject["learningStyleTypeIDs"] = selectedLearningStyles;

  formObject['phoneNumber'] = { 'countryCode': '', 'areaCode': '', 'number': formObject['phoneNumber'], 'extension': '' }

  formObject.sourcePageUrl = window.location.href;
  if (props.ribbonTags && props.ribbonTags.length) {
    let tags = [];
    if (isObject(props.ribbonTags[0])) {
      tags = props.ribbonTags.map((item) => item.tag);
    } else {
      for (let i = 0; i < props.leadGenForm.tagsArray.length; i++) {
        if (isObject(props.leadGenForm.tagsArray[i])) {
          tags.push(props.leadGenForm.tagsArray[i].tag);
        } else {
          tags.push(props.leadGenForm.tagsArray[i]);
        }
      }
    }
    formObject["tagsArray"] = tags;
  }

  function isObject(objValue) {
    return objValue && typeof objValue === "object" && objValue.constructor === Object;
  }

  Object.assign(formObject, applyUtmAttribution({}));
  const hashedData = hashObject(formObject);
  dataLayer.push({
    event: "formSubmitted",
    leadsUserData: hashedData,
  });

  const apiResponse = await processLeadSubmission(formObject)
  if (apiResponse) {
    window.location.href = "/brochurerequest-thankyou-digital";
  } else {
    errMsg.value = "There was a problem with the server response.";
  }
}

function checkErrors() {
  postalcodeError.value = checkInputFields(inputPostalCode.value);
  nameError.value = checkInputFields(inputFirstName.value);
  emailError.value = checkInputFields(inputEmail.value);
  errorFound.value = nameError.value || emailError.value || postalcodeError.value;
}

defineExpose({
  submitHandler,
});

const [{ data: leadGen }, { data: ribbon }] = await Promise.all([
  leadGenData(),
  ribbonFormData(),
]);

ribbonForm = ribbon.value?.ribbonForm ?? null;
leadGenFields = leadGen.value?.leadGenFields ?? null;

const ribbonBg = ribbonForm?.ribbonBackground ?? "white";
const backgroundColor = ribbonBg.toLowerCase().replace(/\s+/g, "-");
const learningStylesLabels = leadGenFields?.learningStylesCheckboxes ?? {};

onMounted(() => {
  const section = document.getElementById("ribbon-form-section");
  if (!section) return;
  const fieldsets = section.getElementsByTagName("fieldset");
  if (!fieldsets[0]) return;
  fieldsets[0].removeAttribute("disabled");
});
</script>

<template>
  <section v-if="ribbonForm && leadGenFields" id="ribbon-form-section" :class="'ribbon-form-section bg-' + backgroundColor" data-load="eager">
    <div class="container">
      <div class="row">
        <div class="col-12 ribbon-form-inner-section">
          <div class="row">
            <div class="col-12 col-md-4">
              <div class="image-container">
                <NuxtImg
                  class="ribbon-image d-none d-md-block"
                  :src="ribbonForm.ribbonImage.url"
                  :alt="getRibbonImageAlt(ribbonForm.ribbonImage, ribbonForm.ribbonImageTitle)"
                  loading="lazy"
                  decoding="async"
                  format="webp"
                />
                <NuxtImg
                  class="ribbon-image d-md-none"
                  :src="ribbonForm.mobileRibbonImage.url"
                  :alt="getRibbonImageAlt(ribbonForm.mobileRibbonImage, ribbonForm.ribbonImageTitle)"
                  loading="lazy"
                  decoding="async"
                  format="webp"
                />
              </div>
            </div>
            <div class="col-12 col-md-8">
              <div class="ribbon-form-container">
                <UIHeading
                  :titleComponent="ribbonForm.titleComponent"
                  :subtitleComponent="ribbonForm.subtitleComponent"
                  :titleClass="'brochure-ribbon-title'"
                  :subtitleClass="'brochure-ribbon-subtitle'"
                />
                <form class="ribbon-form" ref="formData" @submit.prevent="submitHandler">
                  <fieldset disabled="disabled">
                    <div class="row">
                      <div class="col-12 col-md-6">
                        <div class="form-floating input-container">
                          <input type="text" id="firstName" class="form-control" :class="nameError ? 'is-invalid' : 'form-input'" name="firstName" required v-model="inputFirstName" :placeholder="leadGenFields.namePlaceholderText" @focusout="checkErrors()" >
                          <label for="firstName" :class="nameError ? 'error-label' : 'placeholder-label'" v-html="nameError ? leadGenFields.nameErrorMessage : leadGenFields.namePlaceholderText"></label>
                        </div>
                      </div>
                      <div class="col-12 col-md-6">
                        <div class="form-floating input-container">
                          <input type="email" id="emailAddress" class="form-control" :class="invalidEmail || emailError ? 'is-invalid' : 'form-input'" name="emailAddress" required v-model="inputEmail" :placeholder="leadGenFields.emailFields.emailPlaceholderText" @focusout="checkErrors()" @focusin="removeInvalidState()">
                          <label for="emailAddress" class="placeholder-label" :class="!invalidEmail && !emailError ? 'd-block' : 'd-none'" v-html="leadGenFields.emailFields.emailPlaceholderText"></label>
                          <label for="emailAddress" class="error-label" :class="invalidEmail || emailError ? 'd-block': 'd-none'" v-html="emailErrorMessage"></label>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 col-md-6" v-if="ribbonForm.showPhoneNumber">
                        <div class="form-floating input-container">
                          <input
                            type="tel"
                            id="phoneNumber"
                            class="form-control form-input"
                            name="phoneNumber"
                            v-model="inputPhoneNumber"
                            :placeholder="leadGenFields.phonePlaceholderText"
                            @focusout="checkErrors()"
                          />
                          <label
                            for="phoneNumber"
                            v-html="
                              nameError
                                ? leadGenFields.phoneErrorText
                                : leadGenFields.phonePlaceholderText
                            "
                          ></label>
                        </div>
                      </div>
                      <div
                        :class="['col', ribbonForm.showPhoneNumber ? 'col-md-3' : 'col-6']"
                      >
                        <div class="form-floating input-container">
                          <input
                            type="text"
                            id="postalCode"
                            class="form-control form-input"
                            name="postalCode"
                            v-model="inputPostalCode"
                            :placeholder="leadGenFields.zipcodePlaceholderText"
                            @focusout="checkErrors()"
                          />
                          <label
                            for="postalCode"
                            v-html="
                              nameError
                                ? leadGenFields.zipcodeErrorMessage
                                : leadGenFields.zipcodePlaceholderText
                            "
                          ></label>
                        </div>
                      </div>
                      <div
                        :class="['col', ribbonForm.showPhoneNumber ? 'col-md-3' : 'col-6']"
                      >
                        <UIMultiSelectDropdown
                          :inputName="'studentBirthYears'"
                          :options="ages"
                          :multiSelectLabel="leadGenFields.studentAgeTitle"
                        />
                      </div>
                    </div>
                    <div class="form-heading checkbox-heading">
                      <div class="checkbox-title">
                        {{ leadGenFields.titleProgramType }}
                      </div>
                      <div class="checkbox-subtitle">
                        {{ leadGenFields.subTitleProgramType }}
                      </div>
                    </div>
                    <div class="checkbox-group row">
                      <div
                        class="checkbox-container col-12 col-md-6"
                        v-if="!!learningStylesLabels.OnCampusSummerProgramsLabel"
                      >
                        <input
                          type="checkbox"
                          name="learningStyleTypeIDs"
                          class="form-checkbox"
                          value="405000"
                        />
                        <span class="checkmark form-checkmark"></span>
                        <span class="checkbox-label">
                          {{ learningStylesLabels.OnCampusSummerProgramsLabel }}</span
                        >
                      </div>
                      <div
                        class="checkbox-container col-12 col-md-6"
                        v-if="!!learningStylesLabels.OnlineCampsClassesLabel"
                      >
                        <input
                          type="checkbox"
                          name="learningStyleTypeIDs"
                          class="form-checkbox"
                          value="405001"
                        />
                        <span class="checkmark form-checkmark"></span>
                        <span class="checkbox-label">
                          {{ learningStylesLabels.OnlineCampsClassesLabel }}</span
                        >
                      </div>
                      <div
                        class="checkbox-container col-12 col-md-6"
                        v-if="!!learningStylesLabels.SmallGroupOnlineLabel"
                      >
                        <input
                          type="checkbox"
                          name="learningStyleTypeIDs"
                          class="form-checkbox"
                          value="405002"
                        />
                        <span class="checkmark form-checkmark"></span>
                        <span class="checkbox-label">
                          {{ learningStylesLabels.SmallGroupOnlineLabel }}</span
                        >
                      </div>
                      <div
                        class="checkbox-container col-12 col-md-6"
                        v-if="!!learningStylesLabels.OneOnOnePrivateLessonsLabel"
                      >
                        <input
                          type="checkbox"
                          name="learningStyleTypeIDs"
                          class="form-checkbox"
                          value="405003"
                        />
                        <span class="checkmark form-checkmark"></span>
                        <span class="checkbox-label">
                          {{ learningStylesLabels.OneOnOnePrivateLessonsLabel }}</span
                        >
                      </div>
                    </div>
                    <div class="form-error-message" v-html="errMsg" v-if="!!errMsg"></div>
                    <button
                      :class="'btn display btn-primary' + (errorFound ? ' disabled' : '')"
                      type="submit"
                    >
                      {{ ribbonForm.primaryCTAText }}
                    </button>
                    <div class="form-disclaimer" v-if="ribbonForm.showPhoneNumber">
                      By submitting your number, you consent to receive occasional text
                      messages from iD Tech for the purpose of providing more information
                      about our services. Texting and data rates may apply.
                    </div>
                    <div class="form-disclaimer">
                      By signing up you agree to our
                      <a
                        href="#privacy-policy"
                        data-bs-toggle="modal"
                        data-bs-target="#privacy-policy-footer"
                        >Privacy Policy</a
                      >
                    </div>
                  </fieldset>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.brochure-ribbon-title {
  font-weight: bold;
  margin-bottom: 20px;
}

.brochure-ribbon-subtitle {
  font-weight: bold;
}

.ribbon-form-section {
  background-color: $color-secondary;
  color: $color-body-text;
  padding: 50px 0;

  form fieldset[disabled^="disabled"] {
    .form-floating.input-container {
      .form-input.fake-input.form-select {
        background-color: #e9ecef !important;
      }
    }
  }

  &.bg-white {
    background-color: $color-secondary;
  }

  &.bg-light-grey {
    background-color: $color-accent;
  }

  &.bg-grey {
    background-color: $color-gray;
  }

  &.bg-black {
    background-color: $color-primary;
  }

  .ribbon-form-inner-section {
    background: $color-secondary;
    border-radius: 24px;
    padding: 40px 30px;
    border: solid 1px $color-light-gray;

    .image-container {
      display: flex;
      justify-content: center;
      align-items: center;

      .ribbon-image {
        max-width: 100%;
        width: auto;
        height: auto;
        margin-bottom: 20px;
      }
    }

    .ribbon-form-container {
      .ribbon-form {
        .checkbox-group {
          .checkbox-container {
            position: relative;
            .form-checkbox {
              width: 100% !important;
            }
            .checkmark {
              position: absolute;
              left: 15px;
              top: 3px;
            }
            .checkbox-label {
              margin-left: 25px;
            }
          }
        }

        .btn[type="submit"] {
          margin-top: 10px;
          &:disabled {
            opacity: 0.65;
          }
        }

        .form-floating .error-label {
          color: #d43f21;
        }
      }
    }

    .form-error-message {
      padding: 12px 13px;
      display: block;
      text-align: left;
      font-size: $font-size-base;
      border: solid 1px;
      margin-top: 10px;
      color: red;
    }
  }
}

.form-disclaimer {
  padding-top: 20px;
  font-size: $font-size-tiny;

  a {
    color: $color-body-text;
    font-weight: bold;
  }
}

@include media-breakpoint-up(sm) {
  // small screens
}

@include media-breakpoint-up(md) {
  // medium screens
  .ribbon-form-section {
    .image-container {
      .ribbon-image {
        margin-bottom: 0;
      }
    }
  }
}

@include media-breakpoint-up(lg) {
  // large screens
}

@include media-breakpoint-up(xl) {
  // extra large screens
}
</style>
